import * as d3 from 'd3';
import React, { useEffect, useRef, useState, useContext, useCallback } from 'react';
import styled from 'styled-components';
import { useStore } from '../../../store/useStore';
import { ThemeContext } from 'styled-components';

interface SimNode extends d3.SimulationNodeDatum {
  id: string;
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  source: string | SimNode;
  target: string | SimNode;
}

interface GraphData {
  nodes: { id: string; group: string }[];
  links: { source: string; target: string; value: number }[];
}

interface GraphOptions {
  nodeId?: (d: { id?: string; group?: string }) => string | undefined;
  nodeGroup?: (d: { id?: string; group?: string }) => string | undefined;
  nodeGroups?: string[];
  nodeTitle?: (d: { id?: string; group?: string }) => string;
  nodeFill?: string;
  nodeStroke?: string;
  nodeStrokeWidth?: number;
  nodeStrokeOpacity?: number;
  nodeRadius?: number;
  nodeStrength?: number;
  linkSource?: (d: { source: string; target: string }) => string;
  linkTarget?: (d: { source: string; target: string }) => string;
  linkStroke?: string;
  linkStrokeOpacity?: number;
  linkStrokeWidth?: number | ((d: SimLink) => number);
  linkStrokeLinecap?: string;
  linkStrength?: number;
  colors?: string[];
  width?: number;
  height?: number;
  invalidation?: Promise<void>;
}

const Sim = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.text};
  font-family: monospace;
  font-size: 12px;
  padding: 4px 0;
  cursor: pointer;
  transition: opacity 0.2s ease;
  outline: none;
  align-self: flex-start;

  &:hover {
    opacity: 0.6;
  }

  &:active {
    opacity: 0.4;
  }

  &:focus {
    outline: none;
  }
`;
export const Field = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const hasScheduledInitialRender = useRef(false);
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);
  const theme = useContext(ThemeContext);
  const automataState = useStore((state) => state.automataState);

  // Copyright 2021 Observable, Inc.
  // Released under the ISC license.
  // https://observablehq.com/@d3/force-directed-graph
  function ForceGraph(
    { nodes, links }: GraphData,
    {
      nodeId = (d) => d?.id,
      nodeGroup = (d) => d?.group,
      nodeGroups = [] as string[],
      nodeTitle = (d) => `${d?.id}\n${d?.group}`,
      nodeFill = 'currentColor',
      nodeStroke = theme?.text,
      nodeStrokeWidth = 1,
      nodeStrokeOpacity = 1,
      nodeRadius = 2,
      nodeStrength = 1,
      linkSource = ({ source }) => source,
      linkTarget = ({ target }) => target,
      linkStroke = theme?.text,
      linkStrokeOpacity = 0.6,
      linkStrokeWidth = 1,
      linkStrokeLinecap = 'round',
      linkStrength = 1,
      colors = [theme?.text ?? 'black'],
      width = 320,
      height = 120,
    }: GraphOptions = {},
  ) {
    // Compute values.

    const N = d3.map(nodes, nodeId).map(intern);
    const LS = d3.map(links, linkSource).map(intern);
    const LT = d3.map(links, linkTarget).map(intern);
    // @ts-expect-error - d3 internal types
    if (nodeTitle === undefined) nodeTitle = (_, i) => N[i];
    const T = nodeTitle == null ? null : d3.map(nodes, nodeTitle);
    const G = nodeGroup == null ? null : d3.map(nodes, nodeGroup).map(intern);
    const W =
      typeof linkStrokeWidth !== 'function'
        ? null
        : d3.map(
            links,
            linkStrokeWidth as (d: { source: string; target: string; value: number }) => number,
          );

    // Replace the input nodes and links with mutable objects for the simulation.
    const simNodes: SimNode[] = d3.map(nodes, (_, i) => ({ id: N[i] }));
    const simLinks: SimLink[] = d3.map(links, (_, i) => ({ source: LS[i], target: LT[i] }));

    // Compute default domains.
    if (G && nodeGroups === undefined) nodeGroups = d3.sort(G);

    // Construct the scales.
    const color = nodeGroup == null ? null : d3.scaleOrdinal(nodeGroups, colors);

    // Construct the forces.
    const forceNode = d3.forceManyBody();
    const forceLink = d3.forceLink<SimNode, SimLink>(simLinks).id(({ index: i }) => N[i!]);
    if (nodeStrength !== undefined) forceNode.strength(nodeStrength);
    if (linkStrength !== undefined) forceLink.strength(linkStrength);

    const simulation = d3
      .forceSimulation(simNodes)
      .force('link', forceLink)
      .force('charge', forceNode)
      .force('center', d3.forceCenter())
      .on('tick', ticked);

    const svg = d3
      .create('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [-width / 2, -height / 2, width, height])
      .style('border', `5px solid ${theme?.primary}`)
      .attr('style', `max-width: 100%; height: auto; height: intrinsic;`);

    const link = svg
      .append('g')
      .attr('stroke', linkStroke ?? 'black')
      .attr('stroke-opacity', linkStrokeOpacity)
      .attr('stroke-width', typeof linkStrokeWidth !== 'function' ? linkStrokeWidth : null)
      .attr('stroke-linecap', linkStrokeLinecap)
      .selectAll('line')
      .data(simLinks)
      .join('line');

    const node = svg
      .append('g')
      .attr('fill', nodeFill)
      .attr('stroke', nodeStroke ?? 'black')
      .attr('stroke-opacity', nodeStrokeOpacity)
      .attr('stroke-width', nodeStrokeWidth)
      .selectAll('circle')
      .data(simNodes)
      .join('circle')
      .attr('r', nodeRadius)
      // @ts-expect-error - d3 internal types
      .call(drag(simulation));
    // @ts-expect-error - d3 internal types
    if (W) link.attr('stroke-width', ({ index: i }) => W[i]);
    if (G) node.attr('fill', ({ index: i }) => color!(G[i!]));
    if (T) node.append('title').text(({ index: i }) => T[i!]);

    function intern(value: unknown) {
      return value !== null && typeof value === 'object'
        ? (value as { valueOf(): string }).valueOf()
        : (value as string);
    }

    function ticked() {
      link
        // @ts-expect-error - d3 internal types
        .attr('x1', (d) => d.source.x)
        // @ts-expect-error - d3 internal types
        .attr('y1', (d) => d.source.y)
        // @ts-expect-error - d3 internal types
        .attr('x2', (d) => d.target.x)
        // @ts-expect-error - d3 internal types
        .attr('y2', (d) => d.target.y);

      node
        // @ts-expect-error - d3 internal types
        .attr('cx', (d) => d.x)
        // @ts-expect-error - d3 internal types
        .attr('cy', (d) => d.y);
    }

    function drag(simulation: d3.Simulation<SimNode, SimLink>) {
      function dragstarted(event: d3.D3DragEvent<SVGCircleElement, SimNode, SimNode>) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event: d3.D3DragEvent<SVGCircleElement, SimNode, SimNode>) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event: d3.D3DragEvent<SVGCircleElement, SimNode, SimNode>) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3
        .drag<SVGCircleElement, SimNode>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended);
    }

    return Object.assign(svg.node()!, { scales: { color } });
  }

  const makeForce = useCallback(() => {
    return ForceGraph({ nodes: automataState.nodes, links: automataState.links });
  }, [automataState, theme]);

  useEffect(() => {
    if (chartRef.current) {
      d3.select('#automata').selectAll('svg').remove();
      if (svgElement) {
        chartRef.current.appendChild(svgElement);
      }
    }
  }, [svgElement]);

  useEffect(() => {
    if (hasScheduledInitialRender.current) return;
    hasScheduledInitialRender.current = true;

    // Mount-only render for initial graph paint after a short delay.
    const timer = window.setTimeout(() => {
      setSvgElement(makeForce());
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [makeForce]);

  useEffect(() => {
    setSvgElement(makeForce());
  }, [theme]);

  return (
    <>
      <Sim onClick={() => setSvgElement(makeForce())}>[ render state graph ]</Sim>
      <div ref={chartRef} id="automata" />
    </>
  );
};
